import {
  extractSectionFromChapterSectionHeading,
  spliceChapterSectionIntoSource,
} from '@shared/ast/extractChapterSection'
import { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'

import type { ChapterSectionEntry } from './useChapterSectionsBook'
import { type ContentFile, apiService } from '../../../services/api'
import type { ContentAST } from '../../../transforms/ast'
import { logger } from '../../../utils/logger'

/**
 * Shared section-preview state for the organize/elements dialogs: loads the previewed
 * `^`-section's AST slice, renders it editable via SearchPreviewPanel, and saves edits back
 * by splicing into the source file. Resets when the host dialog closes.
 */
export function useSectionPreview({
  open,
  bookId,
  entryByKey,
  onSectionContentSaved,
}: {
  open: boolean
  bookId: string | undefined
  entryByKey: Map<string, ChapterSectionEntry>
  onSectionContentSaved?: (filename: string, updatedFile: ContentFile) => void
}) {
  const [previewKey, setPreviewKey] = useState<string | null>(null)
  const [previewAst, setPreviewAst] = useState<ContentAST | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [previewDirty, setPreviewDirty] = useState(false)
  const [previewSaving, setPreviewSaving] = useState(false)
  const [previewRecentlySaved, setPreviewRecentlySaved] = useState(false)
  const recentlySavedTimerRef = useRef<number | null>(null)
  const sourceCacheRef = useRef<Map<string, ContentFile>>(new Map())

  // Reset preview when the host dialog closes so it doesn't flash stale content on reopen.
  useEffect(() => {
    if (!open) {
      setPreviewKey(null)
      setPreviewAst(null)
      setPreviewError(null)
      setPreviewLoading(false)
      sourceCacheRef.current.clear()
      setPreviewDirty(false)
      setPreviewSaving(false)
      setPreviewRecentlySaved(false)
      if (recentlySavedTimerRef.current) {
        window.clearTimeout(recentlySavedTimerRef.current)
        recentlySavedTimerRef.current = null
      }
    }
  }, [open])

  useEffect(() => {
    return () => {
      if (recentlySavedTimerRef.current) {
        window.clearTimeout(recentlySavedTimerRef.current)
      }
    }
  }, [])

  // Load the previewed section's AST slice when the selection changes.
  useEffect(() => {
    if (!open || !previewKey || !bookId) {
      return
    }
    const entry = entryByKey.get(previewKey)
    if (!entry) {
      setPreviewAst(null)
      setPreviewError(null)
      return
    }
    const filename = entry.chapter.filename
    const headingBlockId = entry.headingBlockId
    let cancelled = false

    const sliceFromSource = (source: ContentFile): void => {
      const ast = source.authorContent
      if (!ast) {
        setPreviewAst(null)
        setPreviewError('Section has no content yet')
        return
      }
      const result = extractSectionFromChapterSectionHeading(ast, headingBlockId)
      if (!result) {
        setPreviewAst(null)
        setPreviewError('Section not found in source file')
        return
      }
      setPreviewAst(result.slice)
      setPreviewError(null)
    }

    const cached = sourceCacheRef.current.get(filename)
    if (cached) {
      sliceFromSource(cached)
      setPreviewLoading(false)
      return
    }

    setPreviewLoading(true)
    setPreviewError(null)
    ;(async () => {
      try {
        const res = await apiService.getChapter(bookId, filename)
        if (cancelled) {
          return
        }
        if (!res.success || !res.data) {
          setPreviewAst(null)
          setPreviewError('Failed to load preview')
          return
        }
        sourceCacheRef.current.set(filename, res.data)
        sliceFromSource(res.data)
      } catch (err) {
        if (cancelled) {
          return
        }
        logger.warn('Section preview load failed', err)
        setPreviewAst(null)
        setPreviewError('Failed to load preview')
      } finally {
        if (!cancelled) {
          setPreviewLoading(false)
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [open, previewKey, bookId, entryByKey])

  const resetEditState = useCallback(() => {
    setPreviewDirty(false)
    setPreviewSaving(false)
    setPreviewRecentlySaved(false)
    if (recentlySavedTimerRef.current) {
      window.clearTimeout(recentlySavedTimerRef.current)
      recentlySavedTimerRef.current = null
    }
  }, [])

  const openPreview = useCallback(
    (key: string) => {
      if (previewKey === key) {
        return
      }
      if (previewDirty && !window.confirm('Discard unsaved changes to this section?')) {
        return
      }
      // Clear stale AST/error/loading FIRST so the inner LexicalEditor (mount-only on initialContent)
      // unmounts during the intermediate render; the effect on previewKey then refetches/slices and
      // remounts it with the correct AST.
      setPreviewAst(null)
      setPreviewError(null)
      setPreviewLoading(true)
      setPreviewKey(key)
      resetEditState()
    },
    [previewKey, previewDirty, resetEditState],
  )

  const closePreview = useCallback(() => {
    if (previewDirty && !window.confirm('Discard unsaved changes to this section?')) {
      return
    }
    setPreviewKey(null)
    resetEditState()
  }, [previewDirty, resetEditState])

  const onPreviewChange = useCallback(() => {
    setPreviewDirty(true)
    setPreviewRecentlySaved(false)
  }, [])

  const onPreviewSave = useCallback(
    async (flushedAst: ContentAST) => {
      if (!previewKey || !bookId) {
        return
      }
      const entry = entryByKey.get(previewKey)
      if (!entry) {
        return
      }
      const filename = entry.chapter.filename
      const headingBlockId = entry.headingBlockId
      // Re-fetch source so we splice into the freshest version, not a possibly-stale cached copy.
      setPreviewSaving(true)
      try {
        const res = await apiService.getChapter(bookId, filename)
        if (!res.success || !res.data?.authorContent) {
          toast.error('Failed to load source for save')
          return
        }
        const nextSource = spliceChapterSectionIntoSource(res.data.authorContent, headingBlockId, flushedAst.blocks)
        if (!nextSource) {
          toast.error('Section heading no longer found in source — save aborted')
          return
        }
        const saveRes = await apiService.updateChapter(
          bookId,
          filename,
          { authorContent: nextSource },
          { returnFull: true },
        )
        if (!saveRes.success || !saveRes.data) {
          toast.error('Failed to save section')
          return
        }
        sourceCacheRef.current.set(filename, saveRes.data)
        setPreviewDirty(false)
        setPreviewRecentlySaved(true)
        if (recentlySavedTimerRef.current) {
          window.clearTimeout(recentlySavedTimerRef.current)
        }
        recentlySavedTimerRef.current = window.setTimeout(() => {
          setPreviewRecentlySaved(false)
          recentlySavedTimerRef.current = null
        }, 1500)
        onSectionContentSaved?.(filename, saveRes.data)
      } catch (err) {
        logger.error('Section save failed', err)
        toast.error('Failed to save section')
      } finally {
        setPreviewSaving(false)
      }
    },
    [previewKey, bookId, entryByKey, onSectionContentSaved],
  )

  const previewTitle = previewKey ? (entryByKey.get(previewKey)?.displayText ?? null) : null

  return {
    previewKey,
    previewAst,
    previewLoading,
    previewError,
    previewTitle,
    previewOpen: Boolean(previewKey),
    previewDirty,
    previewSaving,
    previewRecentlySaved,
    openPreview,
    closePreview,
    onPreviewChange,
    onPreviewSave,
  }
}
